<div class="page">
  @if (isLoading()) {
    <app-branded-loader [label]="'common.loading' | translate" />
  } @else if (clubStats()) {
    @let stats = clubStats()!;
    
    <button type="button" (click)="goBack()" class="back-link mb-5">
      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
      </svg>
      {{ 'common.back' | translate }}
    </button>

    <!-- Hero Section -->
    <div class="relative mb-8 overflow-hidden rounded-3xl bg-slate-900 p-6 shadow-xl md:p-10">
      <div class="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[color-mix(in_srgb,var(--region-accent)_28%,transparent)] blur-3xl" aria-hidden="true"></div>

      <div class="relative">

        <div class="flex flex-col md:flex-row items-center md:items-end gap-8">
          <!-- Big Logo -->
          <div class="accent-gradient w-28 h-28 md:w-36 md:h-36 rounded-full p-1 shrink-0">
             <div class="w-full h-full rounded-full bg-slate-800 flex items-center justify-center text-6xl font-black text-white">
               {{ stats.name.charAt(0) }}
             </div>
          </div>

          <div class="min-w-0 flex-1 text-center md:text-left">
            <h1 class="profile-title mb-2 text-white">{{ stats.name }}</h1>
            <div class="flex flex-wrap justify-center md:justify-start gap-4 text-slate-300 font-bold uppercase tracking-wider text-sm">
              <span class="text-accent-bright flex items-center gap-1">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {{ stats.activeMemberCount }} {{ 'clubProfile.activeMembers' | translate }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards relative overlap -->
    <div>
      <div class="card mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl p-4">
        <div>
          <p class="text-xs font-black uppercase tracking-widest text-slate-400">{{ 'clubProfile.statsPeriod' | translate }}</p>
          <p class="text-sm font-bold text-slate-700 dark:text-slate-300">{{ statsScopeLabel() }}</p>
        </div>
        <div class="inline-flex flex-wrap items-center gap-2 rounded-xl bg-slate-100 dark:bg-slate-800 p-1" role="group" [attr.aria-label]="'clubProfile.statsPeriod' | translate">
          <button type="button" (click)="setStatsScope('season')"
            [class.accent-solid]="statsScope() === 'season'" [class.text-white]="statsScope() === 'season'"
            class="px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 transition-colors">
            {{ 'clubProfile.season' | translate }}
          </button>
          <select [value]="selectedSeasonStart()" (change)="setSeason($any($event.target).value)" [attr.aria-label]="'clubProfile.season' | translate"
            class="field w-auto rounded-lg py-2 pl-3 text-xs">
            @for (season of availableSeasons(); track season) {
              <option [value]="season" [selected]="season === selectedSeasonStart()">{{ season }}–{{ season + 1 }}</option>
            }
          </select>
          <button type="button" (click)="setStatsScope('all')"
            [class.accent-solid]="statsScope() === 'all'" [class.text-white]="statsScope() === 'all'"
            class="px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 transition-colors">
            {{ 'clubProfile.allTime' | translate }}
          </button>
        </div>
      </div>
      <details class="group/help mb-6">
        <summary class="flex w-fit cursor-pointer list-none items-center gap-2 rounded-full px-3 py-2 text-xs font-bold text-slate-500 hover:bg-white dark:text-slate-400 dark:hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-500 [&::-webkit-details-marker]:hidden">
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v6m0-10v1" stroke-linecap="round" /></svg>
          {{ 'clubsKiosk.statsHelp.title' | translate }}
          <svg class="h-3 w-3 transition-transform group-open/help:rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m6 9 6 6 6-6" stroke-linecap="round" stroke-linejoin="round" /></svg>
        </summary>
        <div class="card mt-3 p-5 sm:p-6">
          <dl class="grid gap-5 sm:grid-cols-2">
            @for (stat of ['sources', 'period', 'distance', 'events', 'podiums', 'members', 'activity']; track stat) {
              <div>
                <dt class="mb-1 text-sm font-bold text-slate-900 dark:text-white">{{ ('clubProfile.statsHelp.' + stat + 'Title') | translate }}</dt>
                <dd class="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{{ ('clubProfile.statsHelp.' + stat) | translate }}</dd>
              </div>
            }
          </dl>
        </div>
      </details>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
        <!-- Total Podiums -->
        <div class="card flex h-full flex-col items-center justify-center p-6">
           <span class="accent-soft mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border" aria-hidden="true"><svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M8 21h8m-4-4v4m-5-17h10v5a5 5 0 01-10 0V4zm0 2H4v1a3 3 0 003 3m10-4h3v1a3 3 0 01-3 3" /></svg></span>
           <span class="text-4xl font-black text-slate-900 dark:text-white">{{ stats.podiums.total }}</span>
           <span class="text-sm font-bold text-slate-500 uppercase tracking-widest mt-2">{{ 'clubProfile.totalPodiums' | translate }}</span>
        </div>
        <!-- Distance -->
        <div class="card flex h-full flex-col items-center justify-center p-6">
           <span class="accent-soft mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border" aria-hidden="true"><svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg></span>
           <span class="text-4xl font-black text-slate-900 dark:text-white mb-2">{{ stats.totalDistance | distance }} km</span>
           <span class="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4">{{ 'clubProfile.totalDistance' | translate }}</span>
           
           <!-- Mini Breakdown Flex -->
           <div class="flex flex-wrap justify-center items-center gap-4 w-full mt-2">
             <div class="flex items-center gap-1" [title]="'disciplines.vtt' | translate">
               <span class="text-xl">🚲</span>
               <span class="font-bold text-slate-700 dark:text-slate-300 text-sm">{{ stats.distanceBreakdown.vtt | distance }} km</span>
             </div>
             <div class="flex items-center gap-1" [title]="'disciplines.trottinette' | translate">
               <span class="text-xl">🛴</span>
               <span class="font-bold text-slate-700 dark:text-slate-300 text-sm">{{ stats.distanceBreakdown.trott | distance }} km</span>
             </div>
             <div class="flex items-center gap-1" [title]="'disciplines.canicross' | translate">
               <span class="text-xl">🏃</span>
               <span class="font-bold text-slate-700 dark:text-slate-300 text-sm">{{ stats.distanceBreakdown.running | distance }} km</span>
             </div>
             <div class="flex items-center gap-1" [title]="'clubProfile.youth' | translate">
               <span class="text-xl">🧒</span>
               <span class="font-bold text-slate-700 dark:text-slate-300 text-sm">{{ stats.distanceBreakdown.runningChild | distance }} km</span>
             </div>
             <div class="flex items-center gap-1" [title]="'disciplines.marche' | translate">
               <span class="text-xl">🥾</span>
               <span class="font-bold text-slate-700 dark:text-slate-300 text-sm">{{ stats.distanceBreakdown.walk | distance }} km</span>
             </div>
           </div>
        </div>
        <!-- Races -->
        <div class="card flex h-full flex-col items-center justify-center p-6">
           <span class="accent-soft mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border" aria-hidden="true"><svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z" /></svg></span>
           <span class="text-4xl font-black text-slate-900 dark:text-white mb-2">{{ stats.totalEvents }}</span>
           <span class="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4">{{ 'clubProfile.totalEvents' | translate }}</span>

           <div class="w-full mt-2 border-t border-slate-100 pt-3 dark:border-slate-700">
             <p class="mb-2 text-xs font-bold text-slate-400">{{ 'clubProfile.latestThreeEvents' | translate }}</p>
             <div class="space-y-2">
               @for (activity of recentActivity().slice(0, 3); track activity.id) {
                 <a [routerLink]="['/race-detail', activity.id]" class="flex items-center justify-between gap-3 rounded-lg px-2 py-1 text-sm hover:bg-slate-50 dark:hover:bg-slate-800">
                   <span class="min-w-0 text-slate-600 dark:text-slate-300">
                     <span class="block truncate font-bold" [title]="activity.name">{{ activity.name || ('common.unknownEvent' | translate) }}</span>
                     <time class="text-xs text-slate-400">{{ activity.date | date:'dd/MM/yyyy' }}</time>
                   </span>
                   <span class="shrink-0 font-black text-accent" [title]="'clubProfile.eventParticipants' | translate:activity.participants">
                     {{ 'clubProfile.participantCount' | translate:activity.participants }}
                   </span>
                 </a>
               }
             </div>
           </div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        <!-- Left Column: Detailed breakdown & Members -->
        <div class="lg:col-span-1 space-y-8">
           <!-- Medals Breakdown -->
           <div class="card p-6">
             <div class="flex flex-col mb-6">
                <h2 class="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">{{ 'clubProfile.podiums' | translate }}</h2>
                <p class="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1">{{ stats.totalEvents }} {{ 'clubProfile.totalEvents' | translate }}</p>
              </div>
              
              <div class="space-y-5">
                
                <!-- Gold -->
                <div class="flex flex-col p-4 bg-yellow-50/50 dark:bg-yellow-900/10 rounded-2xl border border-yellow-100/50 dark:border-yellow-900/20">
                  <div class="flex items-center justify-between w-full mb-3">
                    <div class="flex items-center gap-4">
                      <img src="/assets/club-medal-gold.svg" [alt]="'clubsKiosk.goldMedal' | translate" class="h-14 w-12 shrink-0 drop-shadow-sm">
                      <span class="font-black text-lg text-yellow-700 dark:text-yellow-500 uppercase tracking-widest">{{ 'clubProfile.gold' | translate }}</span>
                    </div>
                    <span class="font-black text-3xl text-slate-900 dark:text-white">{{ stats.podiums.gold.total }}</span>
                  </div>
                  <!-- Breakdown -->
                  <div class="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm px-2 border-t border-yellow-100 dark:border-yellow-900/20 pt-3">
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.gold.breakdown.vtt > 0"><span class="text-xl">🚲</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.gold.breakdown.vtt }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.gold.breakdown.trott > 0"><span class="text-xl">🛴</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.gold.breakdown.trott }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.gold.breakdown.running > 0"><span class="text-xl">🏃</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.gold.breakdown.running }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.gold.breakdown.runningChild > 0"><span class="text-xl">🧒</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.gold.breakdown.runningChild }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.gold.breakdown.walk > 0"><span class="text-xl">🥾</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.gold.breakdown.walk }}</b></div>
                  </div>
                </div>

                <!-- Silver -->
                <div class="flex flex-col p-4 bg-slate-50 dark:bg-slate-700/20 rounded-2xl border border-slate-100 dark:border-slate-700/30">
                  <div class="flex items-center justify-between w-full mb-3">
                    <div class="flex items-center gap-4">
                      <img src="/assets/club-medal-silver.svg" [alt]="'clubsKiosk.silverMedal' | translate" class="h-14 w-12 shrink-0 drop-shadow-sm">
                      <span class="font-black text-lg text-slate-500 dark:text-slate-400 uppercase tracking-widest">{{ 'clubProfile.silver' | translate }}</span>
                    </div>
                    <span class="font-black text-3xl text-slate-900 dark:text-white">{{ stats.podiums.silver.total }}</span>
                  </div>
                  <!-- Breakdown -->
                  <div class="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm px-2 border-t border-slate-100 dark:border-slate-700/30 pt-3">
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.silver.breakdown.vtt > 0"><span class="text-xl">🚲</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.silver.breakdown.vtt }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.silver.breakdown.trott > 0"><span class="text-xl">🛴</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.silver.breakdown.trott }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.silver.breakdown.running > 0"><span class="text-xl">🏃</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.silver.breakdown.running }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.silver.breakdown.runningChild > 0"><span class="text-xl">🧒</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.silver.breakdown.runningChild }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.silver.breakdown.walk > 0"><span class="text-xl">🥾</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.silver.breakdown.walk }}</b></div>
                  </div>
                </div>

                <!-- Bronze -->
                <div class="flex flex-col p-4 bg-orange-50/50 dark:bg-orange-900/10 rounded-2xl border border-orange-100/50 dark:border-orange-900/20">
                  <div class="flex items-center justify-between w-full mb-3">
                    <div class="flex items-center gap-4">
                      <img src="/assets/club-medal-bronze.svg" [alt]="'clubsKiosk.bronzeMedal' | translate" class="h-14 w-12 shrink-0 drop-shadow-sm">
                      <span class="font-black text-lg text-orange-700 dark:text-orange-500 uppercase tracking-widest">{{ 'clubProfile.bronze' | translate }}</span>
                    </div>
                    <span class="font-black text-3xl text-slate-900 dark:text-white">{{ stats.podiums.bronze.total }}</span>
                  </div>
                  <!-- Breakdown -->
                  <div class="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm px-2 border-t border-orange-100 dark:border-orange-900/20 pt-3">
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.bronze.breakdown.vtt > 0"><span class="text-xl">🚲</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.bronze.breakdown.vtt }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.bronze.breakdown.trott > 0"><span class="text-xl">🛴</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.bronze.breakdown.trott }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.bronze.breakdown.running > 0"><span class="text-xl">🏃</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.bronze.breakdown.running }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.bronze.breakdown.runningChild > 0"><span class="text-xl">🧒</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.bronze.breakdown.runningChild }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.bronze.breakdown.walk > 0"><span class="text-xl">🥾</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.bronze.breakdown.walk }}</b></div>
                  </div>
                </div>

                <!-- Chocolate -->
                <div class="flex flex-col p-4 bg-amber-50/50 dark:bg-amber-900/10 rounded-2xl border border-amber-100/50 dark:border-amber-900/20">
                  <div class="flex items-center justify-between w-full mb-3">
                    <div class="flex items-center gap-4">
                      <img src="/assets/club-medal-chocolate.svg" [alt]="'clubsKiosk.chocolateBar' | translate" class="h-14 w-12 shrink-0 drop-shadow-sm">
                      <span class="font-black text-lg text-amber-800 dark:text-amber-500 uppercase tracking-widest">{{ 'clubProfile.chocolate' | translate }}</span>
                    </div>
                    <span class="font-black text-3xl text-slate-900 dark:text-white">{{ stats.podiums.chocolate.total }}</span>
                  </div>
                  <!-- Breakdown -->
                  <div class="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm px-2 border-t border-amber-100 dark:border-amber-900/20 pt-3">
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.chocolate.breakdown.vtt > 0"><span class="text-xl">🚲</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.chocolate.breakdown.vtt }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.chocolate.breakdown.trott > 0"><span class="text-xl">🛴</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.chocolate.breakdown.trott }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.chocolate.breakdown.running > 0"><span class="text-xl">🏃</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.chocolate.breakdown.running }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.chocolate.breakdown.runningChild > 0"><span class="text-xl">🧒</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.chocolate.breakdown.runningChild }}</b></div>
                    <div class="flex items-center gap-1.5" *ngIf="stats.podiums.chocolate.breakdown.walk > 0"><span class="text-xl">🥾</span><b class="text-slate-700 dark:text-slate-300">{{ stats.podiums.chocolate.breakdown.walk }}</b></div>
                  </div>
                </div>

              </div>
           </div>

            <!-- Members Section -->
            <div class="card p-6">
               <div class="flex items-center justify-between mb-6">
                 <h2 class="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">{{ 'clubProfile.members' | translate }}</h2>
                 <span class="accent-solid text-xs px-2.5 py-1 rounded-lg font-black uppercase">{{ stats.activeMemberCount }}</span>
               </div>
               
               <!-- Category Breakdown -->
               <div class="space-y-4">
                 @for (cat of stats.activeMembersBreakdown; track cat.ageGroup) {
                   <div class="group flex flex-col p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/40 hover:bg-white dark:hover:bg-slate-800 transition-all border border-transparent hover:border-slate-100 dark:hover:border-slate-700 hover:shadow-md">
                     <div class="flex items-center justify-between mb-3">
                       <span class="font-black text-slate-800 dark:text-white uppercase text-sm tracking-widest">{{ ('clubProfile.ageGroups.' + cat.ageGroup) | translate }}</span>
                       <span class="text-sm font-black text-slate-500">{{ cat.total }}</span>
                     </div>
                     
                     <div class="flex items-center gap-6">
                       <div class="flex items-center gap-4">
                         @if (cat.homme > 0) {
                           <div class="flex items-center gap-2">
                              <span class="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400" [attr.title]="'clubProfile.maleMembers' | translate">
                                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" role="img" [attr.aria-label]="'clubProfile.maleMembers' | translate">
                                  <circle cx="9" cy="15" r="5" />
                                  <path d="m13 11 7-7m-6 0h6v6" />
                                </svg>
                              </span>
                              <span class="text-xs font-bold text-slate-600 dark:text-slate-400">{{ cat.homme }}</span>
                           </div>
                         }
                         @if (cat.femme > 0) {
                           <div class="flex items-center gap-2">
                              <span class="flex h-8 w-8 items-center justify-center rounded-xl bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400" [attr.title]="'clubProfile.femaleMembers' | translate">
                                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" role="img" [attr.aria-label]="'clubProfile.femaleMembers' | translate">
                                  <circle cx="12" cy="8" r="5" />
                                  <path d="M12 13v8m-4-4h8" />
                                </svg>
                              </span>
                              <span class="text-xs font-bold text-slate-600 dark:text-slate-400">{{ cat.femme }}</span>
                           </div>
                         }
                       </div>
                       <!-- Progress Bar -->
                       <div class="flex-1 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden self-center">
                          <div class="h-full bg-[var(--region-accent)] rounded-full transition-all duration-1000" [style.width.%]="(cat.total / (stats.activeMemberCount || 1)) * 100"></div>
                       </div>
                     </div>
                   </div>
                 } @empty {
                   <div class="p-8 text-center border-2 border-dashed border-slate-100 dark:border-slate-700 rounded-2xl">
                     <p class="text-slate-400 italic text-sm">{{ 'clubProfile.noMembers' | translate }}</p>
                   </div>
                 }
               </div>
            </div>
            <div class="rounded-3xl border border-slate-100 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-800">
              <div class="mb-4 flex items-center gap-3">
                <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl accent-soft">
                  <svg class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><ellipse cx="5" cy="9" rx="2.3" ry="3" transform="rotate(-25 5 9)"/><ellipse cx="10" cy="5" rx="2.2" ry="3"/><ellipse cx="16" cy="5.5" rx="2.2" ry="3" transform="rotate(15 16 5.5)"/><ellipse cx="20" cy="10" rx="2.2" ry="3" transform="rotate(30 20 10)"/><path d="M5.5 18c0-3 3-7 6.5-7s6.5 4 6.5 7c0 3-3 3.5-6.5 2-3.5 1.5-6.5 1-6.5-2Z"/></svg>
                </span>
                <h2 class="text-lg font-black uppercase tracking-tight text-slate-900 dark:text-white">{{ 'clubProfile.leadingBreed' | translate }}</h2>
              </div>
              @for (breed of leadingDogBreeds(); track breed.name) {
                <div class="mb-2 flex items-center justify-between gap-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-900/40">
                  <span class="font-bold text-slate-800 dark:text-white">{{ breed.name }}</span>
                  <span class="shrink-0 text-sm font-black text-accent">{{ 'clubProfile.dogCount' | translate:breed.count }}</span>
                </div>
              } @empty {
                <p class="text-sm text-slate-500 dark:text-slate-400">{{ 'clubProfile.noBreedData' | translate }}</p>
              }
              @if (dogBreedStats().length) {
                <details class="group/breeds mt-3">
                  <summary class="w-fit cursor-pointer rounded-lg px-2 py-1 text-xs font-bold text-accent hover:bg-slate-100 dark:hover:bg-slate-800">
                    <span class="group-open/breeds:hidden">{{ 'clubProfile.showMoreBreeds' | translate }}</span>
                    <span class="hidden group-open/breeds:inline">{{ 'clubProfile.showLessBreeds' | translate }}</span>
                  </summary>
                  <p class="mb-3 mt-4 text-xs font-bold text-slate-500 dark:text-slate-400">{{ 'clubProfile.allBreeds' | translate }} · {{ statsScopeLabel() }}</p>
                  <ul class="space-y-2">
                    @for (breed of dogBreedStats(); track breed.name) {
                      <li class="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-slate-900/40">
                        <span class="text-slate-700 dark:text-slate-200">{{ breed.name }}</span>
                        <b class="shrink-0 text-slate-900 dark:text-white">{{ 'clubProfile.dogCount' | translate:breed.count }}</b>
                      </li>
                    }
                  </ul>
                </details>
              }
              <p class="mt-3 text-xs leading-relaxed text-slate-400">{{ 'clubProfile.breedExplanation' | translate }}</p>
            </div>
        </div>

        <!-- Right Column: Recent Activity -->
        <div class="lg:col-span-2">
          <div class="card h-full p-4 sm:p-8">
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 px-2">
              <div class="flex items-center gap-4">
                <div class="accent-soft w-12 h-12 rounded-2xl flex items-center justify-center">
                  <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h2 class="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">{{ 'clubProfile.recentActivity' | translate }}</h2>
                  <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">{{ 'clubProfile.recentActivityDescription' | translate }}</p>
                </div>
              </div>
            </div>
            
            <div class="space-y-4">
              @for (activity of recentActivity(); track activity.id) {
                <article class="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-900/40">
                  <div class="flex flex-wrap items-start justify-between gap-3">
                    <a [routerLink]="['/race-detail', activity.id]" class="text-lg font-black leading-tight text-slate-900 hover:text-[color:var(--accent-logo)] dark:text-white">
                      {{ activity.name || ('common.unknownEvent' | translate) }}
                    </a>
                    <time class="text-xs font-bold text-slate-500 dark:text-slate-400">{{ activity.date | date:'dd/MM/yyyy' }}</time>
                  </div>
                  <p class="mt-2 text-sm font-bold text-slate-600 dark:text-slate-300">{{ 'clubProfile.eventParticipants' | translate:activity.participants }}</p>
                  <div class="mt-4 flex flex-wrap gap-2">
                    @for (discipline of activity.disciplines; track discipline.discipline) {
                      <span class="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        {{ getDisciplineLabel(discipline.discipline) }} · <b>{{ discipline.participants }}</b>
                      </span>
                    }
                  </div>
                  @if (activity.medals.length) {
                    <div class="mt-5 border-t border-slate-200 pt-4 dark:border-slate-700">
                      <h5 class="mb-3 text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">{{ 'clubProfile.disciplineMedals' | translate }}</h5>
                      <div class="space-y-2">
                        @for (medals of activity.disciplineMedals; track medals.discipline) {
                          <div class="flex flex-wrap items-center justify-between gap-3 text-sm">
                            <b class="text-slate-700 dark:text-slate-200">{{ getDisciplineLabel(medals.discipline) }}</b>
                            <ng-container [ngTemplateOutlet]="activityMedalCounts" [ngTemplateOutletContext]="{ $implicit: medals }" />
                          </div>
                        }
                      </div>
                      <details class="mt-4">
                        <summary class="w-fit cursor-pointer rounded-lg px-2 py-1 text-xs font-bold text-accent hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:bg-slate-800">{{ 'clubProfile.showAllStats' | translate }}</summary>
                        <h5 class="mb-3 mt-4 text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">{{ 'clubProfile.activityMedals' | translate }}</h5>
                      <div class="space-y-3">
                        @for (medals of activity.medals; track medals.key) {
                          <div class="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white px-3 py-2 dark:bg-slate-800">
                            <div class="text-sm text-slate-700 dark:text-slate-200">
                              <b>{{ getDisciplineLabel(medals.discipline) }}</b>
                              <span class="ml-2">{{ medals.category || ('clubProfile.categoryUnknown' | translate) }}</span>
                              @if (medals.ranking === 'overall') {
                                <span class="block text-xs text-slate-500 dark:text-slate-400">{{ 'clubProfile.overallRanking' | translate }}</span>
                              }
                            </div>
                            <div class="flex items-center gap-3">
                              @for (medal of [
                                { kind: 'gold', count: medals.gold, label: 'clubsKiosk.goldMedal' },
                                { kind: 'silver', count: medals.silver, label: 'clubsKiosk.silverMedal' },
                                { kind: 'bronze', count: medals.bronze, label: 'clubsKiosk.bronzeMedal' },
                                { kind: 'chocolate', count: medals.chocolate, label: 'clubsKiosk.chocolateBar' }
                              ]; track medal.kind) {
                                @if (medal.count) {
                                  <span class="inline-flex items-center gap-1 text-sm font-black text-slate-800 dark:text-white" [attr.title]="medal.label | translate">
                                    <img [src]="'/assets/club-medal-' + medal.kind + '.svg'" [alt]="medal.label | translate" class="h-9 w-8">
                                    {{ medal.count }}
                                  </span>
                                }
                              }
                            </div>
                          </div>
                          <ul class="space-y-1 px-3 text-sm text-slate-600 dark:text-slate-300">
                            @for (winner of medals.winners; track winner.id) {
                              <li class="flex items-center gap-2">
                                <img [src]="'/assets/club-medal-' + (winner.rank === 1 ? 'gold' : winner.rank === 2 ? 'silver' : winner.rank === 3 ? 'bronze' : 'chocolate') + '.svg'" [alt]="(winner.rank === 1 ? 'clubsKiosk.goldMedal' : winner.rank === 2 ? 'clubsKiosk.silverMedal' : winner.rank === 3 ? 'clubsKiosk.bronzeMedal' : 'clubsKiosk.chocolateBar') | translate" class="h-6 w-5">
                                <span>#{{ winner.rank }}</span>
                                <span>{{ winner.name || ('clubProfile.unknownParticipant' | translate) }} <app-registered-star [humanId]="winner.humanId" /></span>
                              </li>
                            }
                          </ul>
                        }
                      </div>
                      </details>
                    </div>
                  }
                </article>
              }
              
              @if (recentActivity().length === 0) {
                <div class="flex flex-col items-center justify-center py-20 text-center">
                    <div class="w-20 h-20 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center mb-4 text-slate-300 dark:text-slate-600"><svg class="h-9 w-9" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z" /></svg></div>
                    <p class="text-slate-500 font-bold uppercase tracking-widest text-sm">{{ 'clubProfile.noRecentActivity' | translate }}</p>
                    <p class="text-slate-400 text-xs mt-1 italic">{{ 'clubProfile.noRecentActivityDescription' | translate }}</p>
                </div>
              }
            </div>
          </div>
        </div>
      </div>

    </div>
  } @else {
    <div class="text-center py-20 text-slate-500">
      {{ 'clubProfile.notFound' | translate }}
    </div>
  }
</div>
<ng-template #activityMedalCounts let-medals>
  <div class="flex items-center gap-3">
    @for (medal of [
      { kind: 'gold', count: medals.gold, label: 'clubsKiosk.goldMedal' },
      { kind: 'silver', count: medals.silver, label: 'clubsKiosk.silverMedal' },
      { kind: 'bronze', count: medals.bronze, label: 'clubsKiosk.bronzeMedal' },
      { kind: 'chocolate', count: medals.chocolate, label: 'clubsKiosk.chocolateBar' }
    ]; track medal.kind) {
      @if (medal.count) {
        <span class="inline-flex items-center gap-1 font-black text-slate-800 dark:text-white" [attr.title]="medal.label | translate">
          <img [src]="'/assets/club-medal-' + medal.kind + '.svg'" [alt]="medal.label | translate" class="h-8 w-7">
          {{ medal.count }}
        </span>
      }
    }
  </div>
</ng-template>
